import {useCallback, useMemo} from 'react';
import type {HomeEnv} from '@chekin/shared/home';
import {useSummary} from 'context/summary';
import {useReservation} from 'context/reservation';
import {useGuestGroup, useIVComputedDetails, usePath} from 'hooks';
import {useBuildNavigationItems} from 'hooks/useBuildNavigationItems';
import {PATHS} from 'routes/constants';
import {POSTHOG_EVENTS, useAnalytics} from 'analytics';
import {getScreenInfo} from 'analytics/posthog/getScreenInfo';

export function useGuestappHomeEnv(): HomeEnv {
  const {data: summary, isSuccess} = useSummary();
  const {data: reservation} = useReservation();
  const {totalGuests, totalRegisteredGuests, totalFullyRegisteredGuests} =
    useGuestGroup();
  const {isIVEnabled} = useIVComputedDetails();
  const {getRegistrationPath, getPaymentPath} = useBuildNavigationItems();
  const {getFullPath} = usePath();
  const {track} = useAnalytics();

  const onNavigate = useCallback<NonNullable<HomeEnv['onNavigate']>>(
    (path, source, extra) => {
      track(POSTHOG_EVENTS.HOME_NAVIGATION, {
        source,
        destination: getScreenInfo(path).area,
        ...extra,
      });
    },
    [track],
  );

  return useMemo(
    () => ({
      reservation,
      summary,
      isSummaryLoaded: Boolean(isSuccess),
      guestStats: {totalGuests, totalRegisteredGuests, totalFullyRegisteredGuests},
      isIVEnabled,
      navigation: {
        registration: getFullPath(getRegistrationPath()),
        payments: getFullPath(getPaymentPath()),
        remoteAccess: getFullPath(PATHS.remoteAccess),
        faq: getFullPath(PATHS.faq),
      },
      onNavigate,
    }),
    [
      reservation,
      summary,
      isSuccess,
      totalGuests,
      totalRegisteredGuests,
      totalFullyRegisteredGuests,
      isIVEnabled,
      getFullPath,
      getRegistrationPath,
      getPaymentPath,
      onNavigate,
    ],
  );
}
